import * as React from "react";
import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  type Placement,
} from "@floating-ui/react";

import { useControlled } from "../forms/useControlled";
import { mergeProps } from "../utils/mergeProps";
import { usePortalScopeClassName } from "./usePortalScope";
import "./overlay.css";

/**
 * Overlay, OverlayTrigger, OverlayPanel and OverlayPanelContent — the popover surface, 19 call
 * sites. Built directly on `@floating-ui/react`, which was already in the tree before this
 * migration began: the vendor is built on it too, so owning this removes a layer rather than
 * adding a dependency.
 *
 * ── WHAT `Overlay` IS ─────────────────────────────────────────────────────────────────
 *
 * Nothing, visually. It renders NO DOM — it is a context provider that owns the open state and
 * the positioning, so the trigger and the panel can sit anywhere in the tree beneath it. The
 * three parts are separate components precisely so a caller can put the trigger inside a
 * toolbar and the panel in a portal without threading refs by hand.
 *
 * ── AN IMPROVEMENT DELIBERATELY NOT MADE HERE ─────────────────────────────────────────
 *
 * `OverlayPanelContent` puts `className` on the OUTER element — the one carrying the surface
 * padding — and `...rest` (so `style`) plus `ref` on the INNER scroll container. Two props that
 * read as describing one box in fact describe two, and `src/components/ui/overlay.tsx` carries
 * a documented workaround: zero the padding on the outer node through a custom property, then
 * re-apply the same spacing on the inner one.
 *
 * Putting both on one element was tried here and reverted. `overlay.test.tsx` asserts the split
 * directly, and the wrapper's workaround is built on it, so "fixing" it inside this swap would
 * have changed a public contract inside a change whose whole promise is that nothing moves.
 *
 * The split is preserved exactly. Simplifying it is a worthwhile follow-up — as its own diff,
 * where the only thing under review is that decision.
 */
interface OverlayContextValue {
  openState: boolean;
  context: ReturnType<typeof useFloating>["context"];
  floatingStyles: React.CSSProperties;
  arrowRef: React.MutableRefObject<SVGSVGElement | null>;
  referenceEl: Element | null;
  setFloating: (node: HTMLElement | null) => void;
  setReference: (node: HTMLElement | null) => void;
  getFloatingProps: (props?: Record<string, unknown>) => Record<string, unknown>;
  getReferenceProps: (props?: Record<string, unknown>) => Record<string, unknown>;
}

const OverlayContext = React.createContext<OverlayContextValue | undefined>(undefined);

function useOverlayContext(): OverlayContextValue {
  const context = React.useContext(OverlayContext);
  if (!context) {
    // A clear failure beats a silently inert panel: without the provider there is no open
    // state and no position, so the panel would render at 0,0 and never close.
    throw new Error("OverlayPanel and OverlayTrigger must be rendered inside an <Overlay>.");
  }
  return context;
}

export interface OverlayProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
}

export function Overlay({ children, onOpenChange, open, placement = "top" }: OverlayProps) {
  const arrowRef = React.useRef<SVGSVGElement | null>(null);
  const [openState, setOpenState] = useControlled<boolean>({ controlled: open, default: false });

  const handleOpenChange = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);
  const { context, floatingStyles, refs } = useFloating({
    open: openState,
    onOpenChange: handleOpenChange,
    placement,
    // `autoUpdate` keeps the panel attached while the page scrolls or the trigger moves; a
    // popover opened inside a scrolling table detaches from its cell without it.
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(11),
      flip(),
      // `limitShift` stops the panel sliding so far to stay on screen that it loses its
      // trigger — the arrow ends up pointing at nothing.
      shift({ limiter: limitShift() }),
      arrow({ element: arrowRef }),
    ],
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([
    useRole(context, { role: "dialog" }),
    useClick(context),
    useDismiss(context),
  ]);

  // Deliberately NOT memoised. The handlers inside close over the caller's `onOpenChange`,
  // which closes over page state; pinning this froze a radio group's handler earlier in this
  // migration and silently dropped answers. The cost is re-rendering two children.
  const value: OverlayContextValue = {
    openState,
    context,
    floatingStyles,
    arrowRef,
    referenceEl,
    setFloating: refs.setFloating,
    setReference: (node: HTMLElement | null) => {
      // Tracked in state, not just in the floating ref, because the portal scope has to react
      // to it: the class list is read from the trigger's provider ancestor.
      setReferenceEl(node);
      refs.setReference(node);
    },
    getFloatingProps,
    getReferenceProps,
  };

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
}

export interface OverlayTriggerProps {
  children?: React.ReactNode;
}

export const OverlayTrigger = React.forwardRef<HTMLElement, OverlayTriggerProps>(
  function OverlayTrigger({ children, ...rest }, ref) {
    const { getReferenceProps, setReference } = useOverlayContext();
    const childRef = React.isValidElement(children)
      ? (children as unknown as { ref?: React.Ref<HTMLElement> }).ref
      : undefined;
    const mergedRef = useMergeRefs([setReference, ref, childRef ?? null]);

    if (!children || !React.isValidElement(children)) return <>{children}</>;

    /*
     * The trigger is the caller's own element — a button, a card, a table cell. Cloning rather
     * than wrapping means no extra box in the layout and no lost styling.
     *
     * MERGED, not spread, and this is a FIX: the first version spread `children.props` last, so
     * a trigger whose button had its own `onClick` replaced the one that opens the overlay. The
     * overlay simply could not be opened — no error, no warning, and every test passed because
     * none attached a handler to the trigger's child.
     */
    return React.cloneElement(children, {
      ...mergeProps(getReferenceProps(rest), children.props as Record<string, unknown>),
      ref: mergedRef,
    } as React.HTMLAttributes<HTMLElement>);
  },
);

export interface OverlayPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export const OverlayPanel = React.forwardRef<HTMLDivElement, OverlayPanelProps>(
  function OverlayPanel({ "aria-labelledby": ariaLabelledby, children, className, ...rest }, ref) {
    const { arrowRef, context, floatingStyles, getFloatingProps, openState, referenceEl, setFloating } =
      useOverlayContext();
    const portalScope = usePortalScopeClassName(referenceEl);
    const mergedRef = useMergeRefs([setFloating, ref]);

    if (!openState) return null;

    return (
      <FloatingPortal>
        {/*
          Scope wrapper — inert in this app, and deliberately so. See ./usePortalScope: the
          density classes live on <html>, so this panel inherits the page's 12px spacing, while
          the VENDOR's portal re-mounts a provider at its default `medium` density and renders
          8px. That is the one measured difference between the two, and ours is the correct side.
        */}
        <div className={portalScope}>
        {/*
          `modal={false}` with `outsideElementsInert` matches the vendor: focus is managed and
          returned, but the rest of the page is not hidden from assistive technology — an
          overlay is a popover, not a modal dialog, and marking the page inert would strip the
          context the popover is about.
        */}
        <FloatingFocusManager context={context} modal={false} outsideElementsInert>
          <div
            aria-labelledby={ariaLabelledby}
            aria-modal="true"
            className={className ? `saltOverlayPanel ${className}` : "saltOverlayPanel"}
            {...getFloatingProps()}
            ref={mergedRef}
            style={floatingStyles}
          >
            <div {...rest}>{children}</div>
            <FloatingArrow
              context={context}
              fill="var(--overlay-background)"
              height={6}
              ref={arrowRef}
              stroke="var(--overlay-borderColor)"
              strokeWidth={1}
              width={12}
            />
          </div>
        </FloatingFocusManager>
        </div>
      </FloatingPortal>
    );
  },
);

export type OverlayPanelContentProps = React.HTMLAttributes<HTMLDivElement>;

export const OverlayPanelContent = React.forwardRef<HTMLDivElement, OverlayPanelContentProps>(
  function OverlayPanelContent({ children, className, ...rest }, ref) {
    const [scrollBarTop, setScrollBarTop] = React.useState(false);
    const [scrollBarBottom, setScrollBarBottom] = React.useState(true);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const divRef = React.useRef<HTMLDivElement | null>(null);
    const mergedRef = useMergeRefs([divRef, ref]);

    const handleScroll = () => {
      const container = divRef.current;
      if (!container) return;
      setScrollBarTop(container.scrollTop > 0);
      setScrollBarBottom(
        container.scrollHeight - container.scrollTop - container.clientHeight !== 0,
      );
    };

    const checkOverflow = React.useCallback(() => {
      const container = divRef.current;
      if (!container) return;
      // The +1 absorbs sub-pixel rounding: without it a box that fits exactly reports itself
      // as overflowing and paints a scroll shadow over content that never scrolls.
      setIsOverflowing(container.scrollHeight > container.offsetHeight + 1);
    }, []);

    React.useLayoutEffect(() => {
      checkOverflow();
      const container = divRef.current;
      if (!container || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(container);
      return () => observer.disconnect();
    }, [checkOverflow]);

    const inner = ["saltOverlayPanelContent-container"];
    if (isOverflowing) inner.push("saltOverlayPanelContent-overflow");
    if (isOverflowing && scrollBarTop) inner.push("saltOverlayPanelContent-scroll-top");
    if (isOverflowing && scrollBarBottom) inner.push("saltOverlayPanelContent-scroll-bottom");

    return (
      // `className` on the OUTER node and `...rest`/`ref` on the INNER one is the vendor's
      // split, kept deliberately — see the note at the top of this file.
      <div
        className={className ? `saltOverlayPanelContent ${className}` : "saltOverlayPanelContent"}
      >
        <div className={inner.join(" ")} onScrollCapture={handleScroll} {...rest} ref={mergedRef}>
          {children}
        </div>
      </div>
    );
  },
);
